import { CommandBarButton, DetailsList, IColumn, IPanelStyles, Label, mergeStyles, Panel, PanelType, Persona, PersonaSize, ScrollablePane, ScrollbarVisibility, SearchBox, SelectionMode, Stack } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { useLazyRemoveUserFromSiteGroupQuery } from "../../src/taskpane/components/_v2/redux/api/sharepoint/api-sharepoint-slice";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import authModule from "../../utilities/AuthModule";
import generalModule from "../../utilities/GeneralModule";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { logger } from "../../utilities/logger";
import { PersonaUser } from "./CMPermissionsAddMembersPanel";

export const scrollStyle = mergeStyles({
    position: "relative",
    height: "calc(100vh - 160px)",
    marginTop: 5,
    marginBottom: 10
});

const panelStyles: Partial<IPanelStyles> = {
    content: {
        paddingLeft: 5,
        paddingRight: 5
    },
    navigation: {
        justifyContent: "space-between",
        marginTop: 5
    }
}



export interface ICMPermissionsMemberListPanelProps {
    panelIsOpen: boolean;
    closePanel: () => void;
    roleAssignment: any;
    users: PersonaUser[];
    isInOneDrive: boolean;
    userDeleted: () => void;
    sharePointListId: string;
}

export const CMPermissionsMemberListPanel = ({ panelIsOpen, closePanel, roleAssignment, users, isInOneDrive, userDeleted, sharePointListId }: ICMPermissionsMemberListPanelProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
    const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories.oneDriveRootDetails);

    const [selectedUsers, setSelectedUsers] = React.useState<PersonaUser[]>(users);
    const [filteredUsers, setFilteredUsers] = React.useState<PersonaUser[]>(users);
    const [busyRemovingUser, setBusyRemovingUser] = React.useState<boolean>(false);

    const [columns, setColumns] = React.useState<IColumn[]>([]);

    const [removeUserFromSiteGroup] = useLazyRemoveUserFromSiteGroupQuery();

    React.useEffect(() => {
        setFilteredUsers(users);
        setSelectedUsers(users);
        setBusyRemovingUser(false);
    },[users]);

    React.useEffect(() => {
        const cols: IColumn[] = [{
            key: 'name',
            name: languageStrings.Name,
            minWidth: 250,
            maxWidth: 250,
            isResizable: true,
            onRender: (item: any) => {
                return (
                    <Persona
                        text={item.text}
                        secondaryText={item.tertiaryText}
                        size={PersonaSize.size40}
                    />
                )
            }
        }];
        
        cols.push(
            {
                key: 'role',
                name: "",
                minWidth: 90,
                maxWidth: 90,
                isResizable: true,
                onRender: (item: PersonaUser) => {
                    return (
                        <>
                            <Stack verticalAlign="center" horizontalAlign="center">
                                <Stack.Item>
                                    <CommandBarButton
                                        styles={{ root: { marginTop: 7, paddingTop: 5, paddingBottom: 5 } }}
                                        text={item.role}
                                        disabled={(busyRemovingUser || !item.canRemoveFromGroup || sharePointListId?.length > 0)}
                                        menuProps={{
                                            items: [
                                                {
                                                    key: 'remove',
                                                    text: languageStrings.Remove, onClick: () => {
                                                        onRemoveUserFromSiteGroup(item);
                                                    }
                                                }
                                            ]
                                        }}
                                    />
                                </Stack.Item>
                            </Stack>
                        </>

                    )
                }
            }
        )

        setColumns(cols)

    }, [busyRemovingUser, filteredUsers, sharePointListId])

    const filterTextChanged = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, filter?: string) => {
        if (filter?.length > 0) {
            setFilteredUsers(selectedUsers.filter(x => (x.text?.toLowerCase().includes(filter?.toLowerCase()) || x.secondaryText?.toLowerCase().includes(filter?.toLowerCase()) || x.tertiaryText?.toLowerCase().includes(filter?.toLowerCase()))))
        } else {
            setFilteredUsers(selectedUsers);
        }
    }

    const onRemoveUserFromSiteGroup = async (item: PersonaUser) => {
        setBusyRemovingUser(true);
        try {
            await removeUserFromSiteGroup({
                siteUrl: isInOneDrive ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
                accessToken: isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
                groupId: item.groupId,
                userId: item.userId
            }).unwrap();
        } catch (error) {
            logger(error, "CMPermissionsMemberListPanel.tsx (onRemoveUserFromSiteGroup)");
        }
        userDeleted();
    }

    return (
        <Panel
            isOpen={panelIsOpen}
            headerText={`${roleAssignment.Member.Title}`}
            onDismiss={() => closePanel()}
            isLightDismiss
            closeButtonAriaLabel="Close"
            type={PanelType.custom}
            customWidth={!generalModule.isInConentMangerMode() ? "300px" : "450px"}
            styles={panelStyles}
        >

            <div style={{ marginLeft: '10px', marginRight: '10px', marginTop: '15px' }}>
                <SearchBox
                    placeholder={languageStrings.Search}
                    onChange={filterTextChanged}
                />
            </div>

            <div className={scrollStyle} >
                <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
                    <div className="xb-content">
                        {selectedUsers?.length > 0 && <DetailsList
                            items={filteredUsers}
                            setKey="set"
                            columns={columns}
                            selectionMode={SelectionMode.none}
                            ariaLabelForSelectionColumn="Toggle selection"
                            ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                            checkButtonAriaLabel="select row"
                        />}
                        {filteredUsers?.length === 0 &&
                            <Stack horizontalAlign="center" styles={{root: {paddingTop: 25}}}>
                                <Label disabled>{languageStrings.MembersNotFound}</Label>
                            </Stack>
                        }
                    </div>
                </ScrollablePane>
            </div>
        </Panel>
    )
};

